// One workspace: its repositories, git access, persisted dependencies, the interactive
// worker, and the way into its terminal. Used on the environment page and the workspaces list.
import { FolderSimple, TerminalWindow } from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";

import type { Workspace } from "../../api/client";
import { useT } from "../../i18n";
import { Field } from "../../ui/Card";
import { Mono } from "../../ui/status";
import { repositoriesQuery } from "../api";

function repoName(url: string): string {
  return url.replace(/^(https?:\/\/|git@)[^/:]+[/:]/, "").replace(/\.git$/, "");
}

export function WorkspaceCard({ workspace, aside }: { workspace: Workspace; aside?: string }) {
  const t = useT().workloads.workspace;
  const repos = useQuery(repositoriesQuery(workspace.id));
  return (
    <div className="rounded-lg border border-muted p-4" data-testid="workspace">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 items-center gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary-lighter text-primary">
            <FolderSimple weight="duotone" className="h-5 w-5" />
          </span>
          <div className="min-w-0">
            <div className="font-semibold text-gray-900">
              {workspace.name}
              {aside ? <span className="font-normal text-gray-500"> · {aside}</span> : null}
            </div>
            <Mono className="block truncate text-gray-500">/workspaces/{workspace.slug}</Mono>
          </div>
        </div>
        <Link
          to={`/workspaces/${workspace.id}/terminal`}
          className="inline-flex h-8 items-center gap-1.5 rounded-md bg-primary px-3 text-[13px] font-semibold text-primary-foreground hover:bg-primary-dark"
        >
          <TerminalWindow weight="duotone" className="h-4 w-4" /> {t.openTerminal}
        </Link>
      </div>
      <ul className="m-0 mt-3 list-none space-y-1 p-0">
        {(repos.data ?? []).map((r) => (
          <li key={r.id} className="flex min-w-0 items-center gap-2 font-mono text-[12.5px] text-gray-700">
            <span className="truncate" title={r.remote_url}>
              {repoName(r.remote_url)}
            </span>
            <span className="text-gray-400">·</span>
            <span className="shrink-0 text-gray-500">{r.default_branch}</span>
          </li>
        ))}
        {repos.isSuccess && repos.data.length === 0 ? <li className="text-[12.5px] text-gray-500">{t.noRepos}</li> : null}
      </ul>
      <dl className="m-0 mt-4 grid grid-cols-2 gap-4 sm:grid-cols-4">
        <Field label={t.git}>{workspace.git_writable ? t.writable : t.readOnly}</Field>
        <Field label={t.dependencies}>{workspace.persist_deps ? t.persisted : t.freshEachRun}</Field>
        <Field label={t.interactiveWorker}>{workspace.interactive_worker_id ? t.running : t.none}</Field>
        <Field label={t.volume} mono>
          <span title={workspace.storage_volume}>{workspace.storage_volume}</span>
        </Field>
      </dl>
    </div>
  );
}
